import type { ReactNode } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

/** Primeiro e último dia do mês corrente (YYYY-MM-DD), sem shift de timezone. */
export function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return {
    from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
    to:   fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  }
}

/** Barra de filtro de período (data de / até) reutilizada pelos relatórios financeiros. */
export function ReportFilters({ from, to, onFrom, onTo, onApply, loading, extra }: {
  from: string
  to: string
  onFrom: (v: string) => void
  onTo: (v: string) => void
  onApply: () => void
  loading?: boolean
  extra?: ReactNode
}) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-4 sm:p-5 mb-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Data inicial" type="date" required value={from}
          onChange={(e) => onFrom(e.target.value)} clearable={false}
          wrapperClassName="w-40"
        />
        <Input
          label="Data final" type="date" required value={to}
          onChange={(e) => onTo(e.target.value)} clearable={false}
          wrapperClassName="w-40"
        />
        {extra}
        <Button onClick={onApply} loading={loading} iconLeft={<Search size={14} />}>Aplicar</Button>
      </div>
    </div>
  )
}

/** Lista de barras horizontais (label + valor) — usada nos breakdowns. */
export function BarList({ data, empty = 'Sem dados.' }: {
  data: { label: string; value: number; valueLabel?: string }[]
  empty?: string
}) {
  if (data.length === 0) return <p className="text-sm text-gray-400">{empty}</p>
  const max = Math.max(...data.map((d) => d.value), 1)
  return (
    <div className="space-y-2.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-40 break-words text-xs text-gray-600" title={d.label}>{d.label}</span>
          <div className="h-5 flex-1 overflow-hidden rounded bg-gray-100">
            <div className="h-full rounded bg-brand-500" style={{ width: `${(d.value / max) * 100}%` }} />
          </div>
          <span className="w-28 text-right text-xs font-semibold text-gray-700">{d.valueLabel ?? d.value}</span>
        </div>
      ))}
    </div>
  )
}
